import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  HardDrive, Plus, Trash2, Loader2, RefreshCw, Search, ChevronDown, ChevronRight,
  FileText, ArrowRightLeft, FilePlus2, AlertTriangle, CheckCircle2, Archive, Hand,
} from "lucide-react";
import { api } from "../../lib/api";

const STATUS_META = {
  update: { label: "Da sostituire", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  new_on_drive: { label: "Nuovo su Drive", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  missing_on_drive: { label: "Assente su Drive", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  no_supplier: { label: "Senza fornitore", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

function SupplierPicker({ suppliers, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = suppliers.find((s) => s.id === value) || null;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const ql = q.trim().toLowerCase();
  const filtered = !ql ? suppliers.slice(0, 40) : suppliers.filter((s) => (s.name || "").toLowerCase().includes(ql)).slice(0, 40);
  return (
    <div ref={ref} className="relative">
      {selected && !open ? (
        <button type="button" onClick={() => { setOpen(true); setQ(""); }} className="w-full border border-ink/20 px-2.5 py-1.5 rounded-sm bg-white text-xs flex items-center justify-between gap-2 text-left" data-testid={`${testid}-selected`}>
          <span className="truncate font-medium text-ink">{selected.name}</span>
          <ChevronDown size={13} className="text-slate-400 shrink-0" />
        </button>
      ) : (
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} autoFocus={open} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder="Scegli fornitore…" className="w-full border border-ink/20 pl-7 pr-2 py-1.5 rounded-sm bg-white text-xs" data-testid={`${testid}-input`} />
        </div>
      )}
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-ink/15 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-results`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">Nessun fornitore</div>
          ) : filtered.map((s) => (
            <button key={s.id} type="button" onClick={() => { onChange(s.id); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-cream2 ${s.id === value ? "bg-cream2 font-semibold" : ""}`} data-testid={`${testid}-opt-${s.id}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ListinoDriveVerify({ suppliers = [] }) {
  const [expanded, setExpanded] = useState(false);
  const [paths, setPaths] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [addingPath, setAddingPath] = useState(false);
  const [scan, setScan] = useState(null);
  const [starting, setStarting] = useState(false);
  const [filter, setFilter] = useState("actionable");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(40);
  const [choices, setChoices] = useState({});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [doneKeys, setDoneKeys] = useState(() => new Set());
  const [confirmItem, setConfirmItem] = useState(null); // {item, supplier_id, action}
  const [confirmDelete, setConfirmDelete] = useState(null); // item
  const pollRef = useRef(null);

  const loadPaths = useCallback(async () => {
    try { const { data } = await api.get("/admin/listino-verify/paths"); setPaths(data.paths || []); } catch { /* */ }
  }, []);
  const loadScan = useCallback(async () => {
    try { const { data } = await api.get("/admin/listino-verify/scan"); setScan(data); return data; } catch { return null; }
  }, []);
  useEffect(() => { if (expanded) { loadPaths(); loadScan(); } }, [expanded, loadPaths, loadScan]);
  useEffect(() => {
    if (scan?.status === "running") {
      pollRef.current = setInterval(async () => {
        const d = await loadScan();
        if (d && d.status !== "running" && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 2500);
      return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }
  }, [scan?.status, loadScan]);

  const addPath = async () => {
    const url = newUrl.trim();
    if (!url) return;
    setAddingPath(true);
    try { await api.post("/admin/listino-verify/paths", { url }); setNewUrl(""); await loadPaths(); toast.success("Percorso aggiunto"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore aggiunta percorso"); }
    finally { setAddingPath(false); }
  };
  const deletePath = async (pid) => {
    try { await api.delete(`/admin/listino-verify/paths/${pid}`); await loadPaths(); } catch { toast.error("Errore eliminazione"); }
  };
  const startScan = async () => {
    setStarting(true); setDoneKeys(new Set()); setChoices({}); setVisible(40);
    try { await api.post("/admin/listino-verify/scan", {}); toast.success("Verifica avviata"); await loadScan(); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossibile avviare la verifica"); }
    finally { setStarting(false); }
  };

  const items = scan?.items || [];
  const counts = items.reduce((acc, i) => { if (!doneKeys.has(i.key)) acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});
  const actionableCount = (counts.update || 0) + (counts.new_on_drive || 0) + (counts.missing_on_drive || 0);

  const ql = query.trim().toLowerCase();
  const shown = items.filter((i) => {
    if (doneKeys.has(i.key)) return false;
    if (filter === "actionable" && i.status === "no_supplier") return false;
    if (["update", "new_on_drive", "missing_on_drive", "no_supplier"].includes(filter) && i.status !== filter) return false;
    if (ql && !(`${i.subfolder_name} ${i.drive_file_name} ${i.db_listino?.filename || ""} ${i.supplier_name || ""}`.toLowerCase().includes(ql))) return false;
    return true;
  });

  const getChoice = (it) => {
    const c = choices[it.key] || {};
    const supplier_id = c.supplier_id ?? it.supplier_id ?? "";
    let action = c.action;
    if (action === undefined) action = it.status === "update" && it.db_listino ? it.db_listino.asset_id : "add";
    return { supplier_id, action };
  };
  const setChoice = (key, patch) => setChoices((p) => ({ ...p, [key]: { ...p[key], ...patch } }));
  const mark = (key, busy) => setBusyKeys((p) => { const n = new Set(p); busy ? n.add(key) : n.delete(key); return n; });

  const doImport = async () => {
    const { item, supplier_id, action } = confirmItem;
    setConfirmItem(null); mark(item.key, true);
    try {
      const body = { supplier_id, drive_file_id: item.drive_file_id, drive_file_name: item.drive_file_name, drive_modified: item.drive_modified, mode: action === "add" ? "add" : "replace" };
      if (action !== "add") body.asset_id = action;
      const { data } = await api.post("/admin/listino-verify/import", body);
      setDoneKeys((p) => new Set(p).add(item.key));
      toast.success(data.mode === "replace" ? "Listino sostituito e in indicizzazione" : "Listino aggiunto e in indicizzazione");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore importazione"); }
    finally { mark(item.key, false); }
  };
  const openConfirm = (it) => {
    const { supplier_id, action } = getChoice(it);
    if (!supplier_id) { toast.error("Seleziona prima il fornitore di destinazione"); return; }
    setConfirmItem({ item: it, supplier_id, action });
  };
  const archiveItem = async (it) => {
    mark(it.key, true);
    try {
      await api.patch(`/admin/suppliers/${it.supplier_id}/assets/${it.db_listino.asset_id}`, { archived: true });
      setDoneKeys((p) => new Set(p).add(it.key));
      toast.success("Listino archiviato (nascosto dalle ricerche)");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore archiviazione"); }
    finally { mark(it.key, false); }
  };
  const deleteItem = async () => {
    const it = confirmDelete; setConfirmDelete(null); mark(it.key, true);
    try {
      await api.delete(`/admin/suppliers/${it.supplier_id}/assets/${it.db_listino.asset_id}`);
      setDoneKeys((p) => new Set(p).add(it.key));
      toast.success("Listino eliminato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
    finally { mark(it.key, false); }
  };
  const leaveItem = (it) => { setDoneKeys((p) => new Set(p).add(it.key)); };

  const FILTERS = [
    ["actionable", `Da gestire (${actionableCount})`],
    ["update", `Da sostituire (${counts.update || 0})`],
    ["new_on_drive", `Nuovi su Drive (${counts.new_on_drive || 0})`],
    ["no_supplier", `Senza fornitore (${counts.no_supplier || 0})`],
  ];

  return (
    <div className="bg-white border border-ink/10 rounded-sm" data-testid="listino-drive-verify">
      <button onClick={() => setExpanded((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="lv-toggle">
        <HardDrive size={18} className="text-ink shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink">Verifica listini da Google Drive</div>
          <div className="text-[11px] text-slate1">Confronta le cartelle Drive con i listini in archivio tramite impronta del file</div>
        </div>
        {actionableCount > 0 && scan?.status === "done" && (
          <span className="text-[10px] font-bold bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="lv-actionable-badge">{actionableCount} da gestire</span>
        )}
        {expanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-ink/10 p-4 space-y-4" data-testid="lv-body">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">Percorsi Drive da verificare</div>
            <div className="space-y-1.5">
              {paths.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-xs bg-cream2/60 border border-ink/10 rounded-sm px-2.5 py-1.5" data-testid={`lv-path-${p.id}`}>
                  <HardDrive size={13} className="text-slate-400 shrink-0" />
                  <span className="font-medium text-ink truncate">{p.name}</span>
                  <span className="text-slate-400 truncate flex-1 hidden sm:block">{p.url}</span>
                  <button onClick={() => deletePath(p.id)} className="text-rose-600 hover:text-rose-700 shrink-0 p-1" title="Rimuovi" data-testid={`lv-path-del-${p.id}`}><Trash2 size={13} /></button>
                </div>
              ))}
              {paths.length === 0 && <div className="text-xs text-slate-400 italic">Nessun percorso. Incolla sotto il link di una cartella Drive condivisa pubblicamente.</div>}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPath()}
                placeholder="https://drive.google.com/drive/folders/…" className="flex-1 border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="lv-new-path-input" />
              <button onClick={addPath} disabled={addingPath || !newUrl.trim()} className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-wider uppercase disabled:opacity-50" data-testid="lv-add-path-btn">
                {addingPath ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Aggiungi
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap border-t border-ink/10 pt-3">
            <button onClick={startScan} disabled={starting || scan?.status === "running" || paths.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.18em] uppercase disabled:opacity-50 hover:bg-ink/90" data-testid="lv-start-scan">
              {scan?.status === "running" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {scan?.status === "running" ? "Verifica in corso…" : "Avvia verifica"}
            </button>
            {scan?.status === "running" && (
              <div className="flex items-center gap-2 text-xs text-slate1" data-testid="lv-progress">
                <div className="w-40 h-1.5 bg-ink/10 rounded-full overflow-hidden">
                  <div className="h-full bg-ink transition-all" style={{ width: `${scan.total ? Math.round((scan.done / scan.total) * 100) : 0}%` }} />
                </div>
                {scan.done}/{scan.total} cartelle
              </div>
            )}
            {scan?.status === "done" && (
              <div className="text-xs text-slate1" data-testid="lv-summary">
                <b className="text-ink">{actionableCount}</b> da gestire · <b className="text-ink">{scan.up_to_date_count || 0}</b> identici (già in archivio) · <b className="text-ink">{counts.no_supplier || 0}</b> senza fornitore
              </div>
            )}
            {scan?.status === "error" && <span className="text-xs text-rose-600">Errore: {scan.error}</span>}
          </div>

          {scan?.status === "done" && items.length > 0 && (
            <div className="space-y-3 border-t border-ink/10 pt-3">
              <div className="text-[11px] text-slate-500 bg-cream2/60 border border-ink/10 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="lv-help">
                <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                <span>Il confronto usa l'<b>impronta</b> del file: i file <b>identici</b> (anche se rinominati) non vengono mostrati. <b className="text-amber-700">Da sostituire</b> = stesso listino ma contenuto diverso e più recente su Drive. <b className="text-emerald-700">Nuovo su Drive</b> = assente in archivio. L'aggiornamento è sempre e solo <b>da Drive verso il tuo archivio</b>.</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {FILTERS.map(([k, lbl]) => (
                  <button key={k} onClick={() => { setFilter(k); setVisible(40); }} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${filter === k ? "bg-ink text-cream border-ink" : "bg-white text-slate1 border-ink/15 hover:border-ink/40"}`} data-testid={`lv-filter-${k}`}>{lbl}</button>
                ))}
                <div className="relative ml-auto">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={query} onChange={(e) => { setQuery(e.target.value); setVisible(40); }} placeholder="Cerca brand o file…" className="border border-ink/20 pl-8 pr-2 py-1.5 rounded-sm bg-white text-xs w-48" data-testid="lv-search" />
                </div>
              </div>

              {shown.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-400" data-testid="lv-no-items">
                  <CheckCircle2 size={22} className="mx-auto mb-2 text-emerald-400" /> Nessun file da gestire con questo filtro.
                </div>
              ) : (
                <div className="space-y-2">
                  {shown.slice(0, visible).map((it) => {
                    const meta = STATUS_META[it.status] || { label: it.status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
                    const busy = busyKeys.has(it.key);
                    const { supplier_id, action } = getChoice(it);
                    const needPicker = it.needs_mapping || it.status === "no_supplier" || !it.supplier_id;
                    const isMissing = it.status === "missing_on_drive";
                    return (
                      <div key={it.key} className="border border-ink/10 rounded-sm p-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`lv-item-${it.key}`}>
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${meta.cls}`} data-testid={`lv-item-status-${it.key}`}>{meta.label}</span>
                            <span className="text-xs font-semibold text-ink truncate">{it.subfolder_name || it.supplier_name}</span>
                            {it.supplier_name && <span className="text-[10px] text-slate-400">· {it.supplier_name}</span>}
                          </div>
                          {!isMissing && (
                            <div className="flex items-center gap-1.5 text-xs min-w-0" data-testid={`lv-drive-file-${it.key}`}>
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-0.5 shrink-0"><HardDrive size={10} /> Drive</span>
                              <span className="truncate text-ink">{it.drive_file_name}</span>
                              <span className="text-slate-400 shrink-0">· {fmtDate(it.drive_modified)}</span>
                            </div>
                          )}
                          {it.db_listino && (
                            <div className="flex items-center gap-1.5 text-xs min-w-0" data-testid={`lv-my-file-${it.key}`}>
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-ink bg-cream2 border border-ink/20 rounded px-1 py-0.5 shrink-0"><FileText size={10} /> Mio file</span>
                              <span className="truncate text-ink">{it.db_listino.filename}</span>
                              <span className="text-slate-400 shrink-0">· {fmtDate(it.db_listino.created_at)}</span>
                              {it.status === "update" && it.newer === "drive" && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 shrink-0" data-testid={`lv-newer-${it.key}`}>Drive più recente · da sostituire</span>}
                              {it.status === "update" && it.newer !== "drive" && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-sky-800 bg-sky-100 border border-sky-300 rounded px-1 py-0.5 shrink-0" data-testid={`lv-newer-${it.key}`}>Contenuto diverso</span>}
                            </div>
                          )}
                          {it.status === "new_on_drive" && <div className="text-[11px] text-slate-400 italic">Assente in archivio — verrà aggiunto come nuovo listino</div>}
                          {isMissing && <div className="text-[11px] text-rose-600 italic">Non più presente su Drive — scegli cosa farne</div>}
                        </div>

                        {isMissing ? (
                          <div className="flex items-center gap-2 shrink-0 flex-wrap" data-testid={`lv-missing-actions-${it.key}`}>
                            <button onClick={() => leaveItem(it)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/20 text-slate1 rounded-sm text-[11px] tracking-wider uppercase hover:bg-cream2 disabled:opacity-50" data-testid={`lv-leave-${it.key}`}><Hand size={13} /> Lascia</button>
                            <button onClick={() => archiveItem(it)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-sm text-[11px] tracking-wider uppercase hover:bg-slate-800 disabled:opacity-50" data-testid={`lv-archive-${it.key}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />} Archivia</button>
                            <button onClick={() => setConfirmDelete(it)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 text-white rounded-sm text-[11px] tracking-wider uppercase hover:bg-rose-700 disabled:opacity-50" data-testid={`lv-delete-${it.key}`}><Trash2 size={13} /> Elimina</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            <div className="w-44">
                              {needPicker ? (
                                <SupplierPicker suppliers={suppliers} value={supplier_id} onChange={(v) => setChoice(it.key, { supplier_id: v })} testid={`lv-sup-${it.key}`} />
                              ) : (
                                <div className="text-xs text-ink font-medium px-2 py-1.5 bg-cream2/60 rounded-sm border border-ink/10 truncate" data-testid={`lv-sup-fixed-${it.key}`}>{it.supplier_name}</div>
                              )}
                            </div>
                            <select value={action} onChange={(e) => setChoice(it.key, { action: e.target.value })} className="border border-ink/20 rounded-sm bg-white text-xs py-1.5 px-2 max-w-[180px]" data-testid={`lv-action-${it.key}`}>
                              <option value="add">Aggiungi come nuovo</option>
                              {(it.existing_listini || []).map((a) => (<option key={a.asset_id} value={a.asset_id}>Sostituisci: {a.filename}</option>))}
                            </select>
                            <button onClick={() => openConfirm(it)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-[11px] tracking-wider uppercase hover:bg-emerald-700 disabled:opacity-50" data-testid={`lv-import-${it.key}`}>
                              {busy ? <Loader2 size={13} className="animate-spin" /> : action === "add" ? <FilePlus2 size={13} /> : <ArrowRightLeft size={13} />}
                              {action === "add" ? "Aggiungi" : "Sostituisci"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {shown.length > visible && (
                    <button onClick={() => setVisible((v) => v + 40)} className="w-full py-2 text-xs text-ink border border-ink/15 rounded-sm hover:bg-cream2" data-testid="lv-show-more">
                      Mostra altri ({shown.length - visible})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {confirmItem && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="lv-confirm-modal">
          <div className="bg-white rounded-sm max-w-md w-full p-5 space-y-4">
            <div className="flex items-center gap-2 text-ink"><AlertTriangle size={18} className="text-amber-500" /><div className="font-display text-lg">Conferma importazione</div></div>
            <div className="text-sm text-slate-600 space-y-2">
              <p>Stai per importare da Drive il file:</p>
              <p className="font-medium text-ink break-all">{confirmItem.item.drive_file_name}</p>
              <p>nel fornitore <b className="text-ink">{suppliers.find((s) => s.id === confirmItem.supplier_id)?.name || "—"}</b>{" "}
                {confirmItem.action === "add" ? "come nuovo listino." : <>sostituendo il listino <b className="text-ink">{(confirmItem.item.existing_listini || []).find((a) => a.asset_id === confirmItem.action)?.filename || confirmItem.item.db_listino?.filename}</b>.</>}</p>
              <p className="text-[12px] text-slate-500">Il file verrà scaricato, caricato in ARCHI-HUB e re-indicizzato per il Preventivatore.</p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmItem(null)} className="px-4 py-2 text-xs tracking-wider uppercase border border-ink/20 rounded-sm text-slate1 hover:bg-cream2" data-testid="lv-confirm-cancel">Annulla</button>
              <button onClick={doImport} className="px-4 py-2 text-xs tracking-wider uppercase bg-emerald-600 text-white rounded-sm hover:bg-emerald-700" data-testid="lv-confirm-ok">Conferma e importa</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="lv-delete-modal">
          <div className="bg-white rounded-sm max-w-md w-full p-5 space-y-4">
            <div className="flex items-center gap-2 text-ink"><Trash2 size={18} className="text-rose-600" /><div className="font-display text-lg">Elimina listino</div></div>
            <div className="text-sm text-slate-600 space-y-2">
              <p>Eliminare definitivamente dal tuo archivio il listino:</p>
              <p className="font-medium text-ink break-all">{confirmDelete.db_listino?.filename}</p>
              <p className="text-[12px] text-slate-500">L'operazione non è reversibile. Se preferisci conservarlo, usa invece «Archivia».</p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-xs tracking-wider uppercase border border-ink/20 rounded-sm text-slate1 hover:bg-cream2" data-testid="lv-delete-cancel">Annulla</button>
              <button onClick={deleteItem} className="px-4 py-2 text-xs tracking-wider uppercase bg-rose-600 text-white rounded-sm hover:bg-rose-700" data-testid="lv-delete-ok">Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
